/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import type { GraphQLSingularResponse } from 'relay-runtime';
import { getATLContextDomain } from '@atlassian/atl-context';
import { getInstance } from '@atlassian/heartbeat/services/heartbeat';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import { getRelayExperimentalApis } from '@atlassian/relay-experimental-apis';
// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import { RETRY_ATTEMPTS_LIMIT } from '@atlassian/relay-fetch/src/utils/retries';
import { createAri } from '@jira/platform__utils-ari/src/index.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import getXsrfToken from '@jira/platform__utils-xsrf-token/src/index.tsx';

let redirecting = false;

/**
 * Resets the internal redirecting variable
 * @deprecated DO NOT USE, FOR TESTING ONLY!
 */
export const forTestOnlyResetRedirecting = (): void => {
	redirecting = false;
};

const markAsRedirecting = () => {
	redirecting = true;
};

if (typeof window !== 'undefined') {
	// Mark as redirecting, when we're unloading the page to prevent double/unwatend redirection.
	window.addEventListener('beforeunload', () => {
		markAsRedirecting();
	});
}

const redirect = (): void => {
	const identity = getATLContextDomain('id');

	// Hardcoding to Jira as this will be the only adopter of this service for now.
	const redirectUrl = `https://${identity}/login?application=jira&continue=${encodeURIComponent(
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.location.href,
	)}`;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.location.href = redirectUrl;
};

/**
 * Checks if the user is authenticated.
 * @returns true if the user will be redirected to login page.
 * @sideEffect sets the redirecting flag to true if the user is not authenticated.
 * @sideEffect changes window.location.href to the login page if the user is not authenticated.
 */
export const checkAuthentication = (): boolean => {
	if (process.env.NODE_ENV !== 'development') {
		if (!__SERVER__ && !redirecting) {
			const heartbeatService = getInstance();
			if (heartbeatService.isInitialized() && !heartbeatService.isAuthenticated()) {
				if (!redirecting) {
					redirect();
				}

				// Set flag to prevent multiple redirects
				markAsRedirecting();
			}
		}
	}
	return redirecting;
};

// Silence error logs from queries in this list
export const blocklist: string[] = ['issueAggQuery'];

export const getRetryAttemptsForQuery = (_queryName: string, operationKind: string): 1 | 0 => {
	if (operationKind === 'query') {
		return RETRY_ATTEMPTS_LIMIT;
	}

	return 0;
};

export const getTraceIdFromExtensions = ({
	extensions,
}: GraphQLSingularResponse): string | undefined => {
	if (
		extensions !== undefined &&
		extensions.gateway !== undefined &&
		extensions.gateway !== null &&
		typeof extensions.gateway.trace_id === 'string'
	) {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		return extensions.gateway.trace_id as string;
	}
	return undefined;
};

/**
 * Given a set of options, produce a new set of options for consumption which contains
 * a combination of the given set and the default options
 * @param {RequestOptions} options
 * @param url
 * @returns RequestOptions
 */
export const generateOptions = (
	options: RequestInit = {},
	url: string | null = null,
): {
	body?: BodyInit | null;
	cache?: RequestCache;
	credentials?: RequestCredentials;
	headers?: HeadersInit;
	integrity?: string;
	keepalive?: boolean;
	method?: string;
	mode?: RequestMode;
	priority?: RequestPriority;
	redirect?: RequestRedirect;
	referrer?: string;
	referrerPolicy?: ReferrerPolicy;
	signal?: AbortSignal | null;
	window?: null;
} & {
	headers:
		| {
				'atl-xsrf-token'?: undefined;
				'atl-client-name': string;

				'atl-client-version': string;
				'X-ExperimentalApi': string;
				'atl-attribution': string;
				// required for Unified Graph Store requests
				'X-Query-Context': string;
		  }
		| {
				'atl-xsrf-token': string;
				'atl-client-name': string;

				'atl-client-version': string;
				'X-ExperimentalApi': string;
				'atl-attribution': string;
				// required for Unified Graph Store requests
				'X-Query-Context': string;
		  };
} => {
	// the token fetcher returns undefined if no cookie value was found, also in SSR cookies are unavailable
	const csrfToken = !__SERVER__ ? getXsrfToken() : undefined;

	// If we don't get a token, there is no point trying to add the csrf header
	const csrfTokenHeader = isNil(csrfToken) ? {} : { 'atl-xsrf-token': csrfToken };

	const cloudId = getMeta('ajs-cloud-id');
	const activationId = getMeta('ajs-activation-id');
	const atlWorkspaceId = `ari:cloud:jira:${cloudId}:workspace/${activationId}`;

	const atlAttribution = {
		atlWorkspaceId,
		tenantId: cloudId,
		product: 'Atlassian Jira', // The product to which the upstream service belongs
		service: 'afm-frontend', // Name of the upstream service
	};

	const siteAri = cloudId
		? createAri({
				resourceOwner: 'platform',
				resourceType: 'site',
				resourceId: cloudId,
			})
		: '';

	return merge(
		{}, // New object to ensure the other options aren't modified
		{
			// We have to provide general options here or the headers we provide will override the default ones found
			// here /src/packages/platform/fetch/src/utils/requests.js due to a shallow merge
			...getDefaultOptions(url),
			...options,
		},
		{
			headers: {
				'atl-client-name': 'jira-frontend',
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				'atl-client-version': `${window?.BUILD_KEY ?? ''}::${window?.SSR_BUILD_KEY ?? ''}`,
				'X-ExperimentalApi': getRelayExperimentalApis().join(','),
				'atl-attribution': JSON.stringify(atlAttribution),
				// required for Unified Graph Store requests
				'X-Query-Context': siteAri,
				...csrfTokenHeader,
			},
		},
	);
};

export const shouldRetry = (error: Error): boolean => {
	if (error instanceof FetchError) {
		return [502, 503, 504].includes(error.statusCode);
	}
	return false;
};
